import { router } from "expo-router";

import { log } from "@/logger";
import { s4pApi } from "@/query/apiSlice";
import { store } from "@/store";

export type NotificationHref =
    | `/child/${number}/absences`
    | `/child/${number}/pickups`
    | `/profile/${number}`
    | `/`;

type NotificationQueryTag =
    | "LeaveRequests"
    | "PickupRequests"
    | "Profile"
    | "Profiles";

/**
 * Maps FCM/APNs notification data to an in-app route.
 * Returns null when the payload is missing or the type is unknown.
 */
export function hrefFromNotificationData(
    data: Record<string, unknown> | undefined
): NotificationHref | null {
    log.debug("hrefFromNotificationData", data);
    if (!data || typeof data.type !== "string") {
        return null;
    }

    switch (data.type) {
        case "leave_request": {
            const childId = Number(data.child_id);
            if (!Number.isFinite(childId)) {
                return null;
            }
            return `/child/${childId}/absences`;
        }
        case "student_profile_change": {
            const pipedriveId = Number(data.pipedrive_id);
            if (!Number.isFinite(pipedriveId)) {
                return null;
            }
            return `/profile/${pipedriveId}`;
        }
        case "pickup_request": {
            const childId = Number(data.child_id);
            if (Number.isFinite(childId)) {
                return `/child/${childId}/pickups`;
            }
            return `/`;
        }
        default:
            return null;
    }
}

/**
 * RTK Query tags to invalidate when opening from a notification.
 * Screen remount / refetchOnFocus is not enough: Home stays subscribed,
 * and keepUnusedDataFor keeps unused results for 60s.
 */
export function tagsFromNotificationData(
    data: Record<string, unknown> | undefined
): NotificationQueryTag[] {
    if (!data || typeof data.type !== "string") {
        return [];
    }

    switch (data.type) {
        case "leave_request":
            return ["LeaveRequests"];
        case "pickup_request":
            return ["PickupRequests"];
        case "student_profile_change":
            return ["Profile", "Profiles"];
        default:
            return [];
    }
}

/**
 * Navigates from a tapped push notification when the payload maps to a known route.
 * Invalidates the matching RTK Query cache first so the destination shows fresh data.
 */
export function navigateFromNotificationData(
    data: Record<string, unknown> | undefined
): void {
    const href = hrefFromNotificationData(data);
    if (!href) {
        return;
    }

    const tags = tagsFromNotificationData(data);
    if (tags.length > 0) {
        store.dispatch(s4pApi.util.invalidateTags(tags));
    }
    router.push(href);
}
